import { FC, useState } from 'react';
import { Typography, Grid, Button, Box } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import Carousel from 'react-material-ui-carousel';
import useSWR from 'swr';
import { useAbility } from '@casl/react';
import palette from '../../theme/palette';
import AddOrEditTestimonial from './AddOrEditTestimonial';
import TestimonialCard from './TestimonialCard';
import { Testimonial } from '../../types/Testimonial';
import { reviewBlurbs } from './constants/reviewBlurbs';
import { AbilityContext } from '../../context/AbilityContext';
import ScrollToTop from '../utils/ScrollToTop';

export const Testimonials: FC = () => {
  const [openAddOrEditDialog, setOpenAddOrEditDialog] = useState(false);
  const ability = useAbility(AbilityContext);

  const { data: testimonials } = useSWR<Testimonial[]>('/testimonials', { suspense: true });

  return (
    <>
      <ScrollToTop />
      <Grid container sx={{ width: '100%' }}>
        <Grid
          item
          container
          sx={{
            marginTop: '70px',
            backgroundColor: palette.paper.secondary,
            display: 'flex',
            padding: { xs: '16px', md: '20px' },
            color: palette.text.contrast,
            width: '100%',
          }}
        >
          <Typography variant="h4" style={{ fontWeight: 600 }}>
            Testimonials
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            width: '100%',
            backgroundColor: palette.paper.tertiary,
            paddingBottom: { xs: '16px', md: '20px' },
          }}
        >
          <Carousel sx={{ width: '100%' }}>
            {reviewBlurbs.map((reviewBlurb) => (
              <Grid
                key={reviewBlurb.name}
                item
                container
                direction={{ xs: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%' }}
              >
                <Grid
                  item
                  sm={12}
                  md={7}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: { xs: '16px', md: '50px' },
                  }}
                >
                  <Typography variant="h3" sx={{ paddingTop: { xs: '10px', md: '20px' } }}>
                    {reviewBlurb.name}
                  </Typography>
                  <Box sx={{ display: 'flex', paddingTop: { xs: '10px', md: '20px' } }}>
                    <FormatQuoteIcon
                      style={{
                        color: palette.button.primary,
                        transform: 'scale(-1, 1)',
                        fontSize: '3rem',
                      }}
                    />
                    <Typography variant="h5" style={{ alignSelf: 'end', fontStyle: 'italic' }}>
                      {reviewBlurb.description}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  item
                  sm={12}
                  md={5}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: { xs: '16px', md: '20px' },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: palette.disabled,
                      backgroundImage: `url(${reviewBlurb.image})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '50%',
                      width: { xs: '250px', sm: '300px', md: '400px' },
                      height: { xs: '250px', sm: '300px', md: '400px' },
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Carousel>
        </Grid>
        {(ability.can('create', 'All') || ability.can('update', 'All')) && (
          <Grid
            item
            container
            sx={{ padding: { xs: '16px', md: '50px' }, display: 'flex', justifyContent: 'end' }}
          >
            <Button
              style={{ backgroundColor: palette.button.primary, color: palette.white }}
              onClick={() => setOpenAddOrEditDialog(true)}
            >
              Add Testimonial
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid item container sx={{ paddingBottom: { xs: '16px', md: '50px' }, display: 'flex' }}>
        {testimonials &&
          testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
      </Grid>
      {(ability.can('create', 'All') || ability.can('update', 'All')) && (
        <AddOrEditTestimonial
          open={openAddOrEditDialog}
          close={() => setOpenAddOrEditDialog(false)}
          testimonial={null}
        />
      )}
    </>
  );
};

export default Testimonials;
